import AttendanceBotAvatar from 'app/pages/chatbot/components/ui/AttendanceBotAvatar'
import {
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  LayoutGrid,
  MessageSquarePlus,
  RotateCcw,
} from 'lucide-react'
import React, {FC, ReactNode, useEffect, useRef, useState} from 'react'
import {useIntl} from 'react-intl'

import type {VersiAllocation} from '../../hooks/useVersiAllocation'
import {
  type AllocationCounts,
  type AllocationExampleFacts,
  type AllocationRow,
  type ProposalChange,
  countProposalKinds,
  filterChoices,
  isSettingsOnlyProposal,
  parseDuration,
  parseFrequency,
  progressStage,
  projectCounts,
} from '../../utils/teacherAllocationModel'
import type {VersiItem} from '../../utils/versiAllocationSession'
import {AllocationFilterSelect, AllocationProgressBar} from './AllocationShared'
import {ChatSkeleton} from './AllocationSkeleton'
import VersiComposer from './VersiComposer'

/** Rows shown before "Show all changes" on a large proposal. */
const COLLAPSED_ROWS = 6

type Props = {
  /** Either assistant: the client one (useVersiAllocation) or the server one (useVersiServer). */
  versi: VersiAllocation & {retry?: () => void}
  counts: AllocationCounts
  facts: AllocationExampleFacts
  rowByClassKey: Map<string, AllocationRow>
  branchOptions: string[]
  levelOptions: string[]
  branch: string
  level: string
  levelLabel: (level: string) => string
  /** Server flow: branch options are ids; this names them. */
  branchLabel?: (branch: string) => string
  /** Server flow: the starting examples the server phrased from the school's own data. */
  examples?: {main: string[]; more: string[]} | null
  /** Server flow: a single Branch / Level option is locked (no All to switch to). */
  lockSingleOption?: boolean
  /** Server flow: a low-confidence transcript is marked for checking before sending. */
  flagLowConfidence?: boolean
  /** Server flow: the saved conversation is being read — the thread shows a skeleton. */
  loadingConversation?: boolean
  defaultFrequency: string
  onBranchChange: (branch: string) => void
  onLevelChange: (level: string) => void
  onViewGrid: () => void
  onPreviewInGrid: () => void
  footer: ReactNode
}

const Avatar: FC = () => (
  <span className='ta-versi-avatar' aria-hidden>
    <AttendanceBotAvatar />
  </span>
)

/**
 * Assign with AI (handover, Point 4): the same Versi assistant from the landing page and the grid.
 * The starting examples are phrased from the institution's own teachers, subjects and classes;
 * clicking one only fills the composer. Every change arrives as a proposal in the conversation and
 * is saved only by Apply.
 */
const VersiAllocationPage: FC<Props> = ({
  versi,
  counts,
  facts,
  rowByClassKey,
  branchOptions,
  levelOptions,
  branch,
  level,
  levelLabel,
  branchLabel,
  examples: serverExamples,
  lockSingleOption,
  flagLowConfidence,
  loadingConversation,
  defaultFrequency,
  onBranchChange,
  onLevelChange,
  onViewGrid,
  onPreviewInGrid,
  footer,
}) => {
  const intl = useIntl()
  const t = (id: string, values?: Record<string, ReactNode>) =>
    intl.formatMessage({id: `ONBOARDING_WIZARD.TEACHER_ALLOCATION.${id}`}, values as any)
  const [draft, setDraft] = useState('')
  const [moreExamples, setMoreExamples] = useState(false)
  const [expandedProposals, setExpandedProposals] = useState<Set<string>>(() => new Set())
  const threadRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLDivElement>(null)

  const branches = filterChoices(branchOptions)
  const levels = filterChoices(levelOptions)
  const stage = progressStage(counts)

  // Keep the newest message in view.
  useEffect(() => {
    const el = threadRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [versi.items.length, versi.isLoading])

  const send = () => {
    const text = draft.trim()
    if (!text) return
    versi.send(text)
    setDraft('')
  }

  const fillExample = (text: string) => {
    setDraft(text)
    requestAnimationFrame(() => composerRef.current?.querySelector('textarea')?.focus())
  }

  const sample = facts.sample
  const clientExamples: string[] = [
    facts.knownTeacher
      ? t('AI.EXAMPLE_ASSIGN', {
          teacher: facts.knownTeacher.teacher,
          subject: facts.knownTeacher.subject,
          grade: facts.knownTeacher.grade,
          branch: facts.knownTeacher.branch,
        })
      : sample
        ? t('AI.EXAMPLE_ASSIGN_PLACEHOLDER', {
            subject: sample.subject,
            grade: sample.grade,
            branch: sample.branch,
          })
        : t('AI.EXAMPLE_ASSIGN_GENERIC'),
    stage === 'zero'
      ? t('AI.EXAMPLE_ALL')
      : stage === 'partial'
        ? t('AI.EXAMPLE_REMAINING')
        : t('AI.EXAMPLE_REVIEW'),
    sample
      ? t('AI.EXAMPLE_FREQUENCY', {
          subject: sample.subject,
          section: sample.classLabel,
          branch: sample.branch,
        })
      : t('AI.EXAMPLE_FREQUENCY_GENERIC'),
  ]
  const clientMoreExamples: string[] = sample
    ? [
        t('AI.EXAMPLE_CONSECUTIVE', {
          subject: sample.subject,
          section: sample.classLabel,
          branch: sample.branch,
        }),
        t('AI.EXAMPLE_NOT_TAUGHT', {
          subject: sample.subject,
          section: sample.classLabel,
          branch: sample.branch,
        }),
      ]
    : [t('AI.EXAMPLE_CONSECUTIVE_GENERIC'), t('AI.EXAMPLE_NOT_TAUGHT_GENERIC')]
  const examples = serverExamples?.main.length ? serverExamples.main : clientExamples
  const moreExampleList = serverExamples ? serverExamples.more : clientMoreExamples

  const welcomeLine =
    stage === 'zero'
      ? t('AI.WELCOME_ZERO', {total: counts.total})
      : stage === 'partial'
        ? t('AI.WELCOME_PARTIAL', {remaining: counts.remaining})
        : t('AI.WELCOME_FULL', {total: counts.total})

  // A class outside the loaded grid (another level or branch) is named from the proposal itself,
  // never shown as its raw key.
  const sectionLabel = (change: ProposalChange) =>
    rowByClassKey.get(change.classKey)?.label ?? change.className ?? change.classKey
  const describeValue = (change: ProposalChange, value: string, weekly?: number | null) => {
    if (change.kind === 'frequency') {
      const {count, per} = parseFrequency(value, defaultFrequency)
      if (per === 'day' && weekly) return t('AI.PER_DAY_WEEKLY_VALUE', {count, weekly})
      return t(per === 'day' ? 'AI.PER_DAY_VALUE' : 'AI.PER_WEEK_VALUE', {count})
    }
    if (change.kind === 'duration') return t('AI.PERIODS_VALUE', {count: parseDuration(value)})
    return value || t('AI.UNASSIGNED')
  }
  const kindBadge = (change: ProposalChange) => {
    const map: Record<ProposalChange['kind'], [string, string]> = {
      new: ['AI.BADGE_NEW', 'info'],
      replace: ['AI.BADGE_REPLACE', 'warning'],
      unassign: ['AI.BADGE_CLEAR', 'warning'],
      frequency: ['AI.BADGE_WEEKLY', 'info'],
      duration: ['AI.BADGE_CONSECUTIVE', 'info'],
      remove: ['AI.BADGE_REMOVE', 'danger'],
    }
    const [key, tone] = map[change.kind]
    return <span className={`ta-badge ta-badge--${tone}`}>{t(key)}</span>
  }

  const proposalSummary = (changes: ProposalChange[]) => {
    const kinds = countProposalKinds(changes)
    const parts: string[] = []
    if (kinds.new) parts.push(t('AI.SUMMARY_NEW', {count: kinds.new}))
    if (kinds.replace) parts.push(t('AI.SUMMARY_REPLACE', {count: kinds.replace}))
    if (kinds.unassign) parts.push(t('AI.SUMMARY_CLEAR', {count: kinds.unassign}))
    if (kinds.frequency) parts.push(t('AI.SUMMARY_WEEKLY', {count: kinds.frequency}))
    if (kinds.duration) parts.push(t('AI.SUMMARY_CONSECUTIVE', {count: kinds.duration}))
    if (kinds.remove) parts.push(t('AI.SUMMARY_REMOVE', {count: kinds.remove}))
    return t('AI.PROPOSAL_INTRO', {count: changes.length, details: parts.join(', ')})
  }

  const renderProposal = (item: Extract<VersiItem, {kind: 'proposal'}>) => {
    const pending = item.status === 'pending' && versi.pendingItem?.id === item.id
    const expanded = expandedProposals.has(item.id)
    const teacherChanges = item.changes.filter((c) =>
      ['new', 'replace', 'unassign'].includes(c.kind)
    )
    const settingChanges = item.changes.filter(
      (c) => c.kind === 'frequency' || c.kind === 'duration'
    )
    const removals = item.changes.filter((c) => c.kind === 'remove')
    const removalOnly = removals.length === item.changes.length
    const subjects = new Set(item.changes.map((c) => c.subject))
    const branchesTouched = new Set(
      item.changes.map((c) => rowByClassKey.get(c.classKey)?.branch).filter(Boolean)
    )
    const scopeLine =
      subjects.size === 1 && branchesTouched.size === 1
        ? `${[...subjects][0]} · ${[...branchesTouched][0]}`
        : null
    const projected = item.projected ?? projectCounts(counts, item.changes)
    const settingsOnly = isSettingsOnlyProposal(item.changes)
    let budget = expanded ? Infinity : COLLAPSED_ROWS
    const take = <T,>(list: T[]) => {
      const out = list.slice(0, Math.max(0, budget))
      budget -= out.length
      return out
    }
    const shownTeacher = take(teacherChanges)
    const shownSettings = take(settingChanges)
    const shownRemovals = take(removals)
    const hidden =
      item.changes.length - shownTeacher.length - shownSettings.length - shownRemovals.length

    return (
      <div key={item.id} className='ta-msg ta-msg--assistant'>
        <Avatar />
        <div className='ta-msg__stack'>
          <div className='ta-bubble'>
            <p>{item.text || proposalSummary(item.changes)}</p>
            {pending && <p>{t('AI.NOTHING_APPLIED')}</p>}
          </div>
          <div className={`ta-proposal${pending ? '' : ' is-closed'}`}>
            <div className='ta-proposal__head'>
              <strong>
                {t('AI.PROPOSED_CHANGES')} · {item.changes.length}
              </strong>
              {pending ? (
                <span className='ta-badge ta-badge--warning'>{t('AI.NOT_APPLIED')}</span>
              ) : (
                <span className='ta-badge ta-badge--muted'>
                  {t(`AI.STATUS_${item.status.toUpperCase()}`)}
                </span>
              )}
            </div>
            {scopeLine && <div className='ta-proposal__scope'>{scopeLine}</div>}

            {shownTeacher.length > 0 && (
              <table className='ta-proposal__table'>
                <thead>
                  <tr>
                    <th>{t('AI.COL_SECTION')}</th>
                    {!scopeLine && <th>{t('AI.COL_SUBJECT')}</th>}
                    <th>{t('AI.COL_CURRENT_TEACHER')}</th>
                    <th>{t('AI.COL_PROPOSED_TEACHER')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownTeacher.map((c) => (
                    <tr key={c.slotKey + c.kind}>
                      <td>
                        <span className='ta-proposal__section'>{sectionLabel(c)}</span>
                        {kindBadge(c)}
                      </td>
                      {!scopeLine && <td>{c.subject}</td>}
                      <td>{describeValue(c, c.current, c.currentWeekly)}</td>
                      <td>{describeValue(c, c.proposed, c.proposedWeekly)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {shownSettings.length > 0 && (
              <table className='ta-proposal__table'>
                <thead>
                  <tr>
                    <th>{t('AI.COL_SECTION')}</th>
                    {!scopeLine && <th>{t('AI.COL_SUBJECT')}</th>}
                    <th>{t('AI.COL_CURRENT')}</th>
                    <th>{t('AI.COL_PROPOSED')}</th>
                  </tr>
                </thead>
                <tbody>
                  {shownSettings.map((c) => (
                    <tr key={c.slotKey + c.kind}>
                      <td>
                        <span className='ta-proposal__section'>{sectionLabel(c)}</span>
                        {kindBadge(c)}
                      </td>
                      {!scopeLine && <td>{c.subject}</td>}
                      <td>{describeValue(c, c.current, c.currentWeekly)}</td>
                      <td>{describeValue(c, c.proposed, c.proposedWeekly)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {shownRemovals.length > 0 && (
              <>
                <p className='ta-proposal__removal-note'>
                  {t('AI.REMOVAL_NOTE', {count: removals.length})}
                </p>
                <table className='ta-proposal__table'>
                  <thead>
                    <tr>
                      <th>{t('AI.COL_SECTION')}</th>
                      <th>{t('AI.COL_SUBJECT')}</th>
                      <th>{t('AI.COL_CURRENT_TEACHER')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shownRemovals.map((c) => (
                      <tr key={c.slotKey + c.kind}>
                        <td>
                          <span className='ta-proposal__section'>{sectionLabel(c)}</span>
                          {kindBadge(c)}
                        </td>
                        <td>{c.subject}</td>
                        <td>{describeValue(c, c.current)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            {(hidden > 0 || expanded) && item.changes.length > COLLAPSED_ROWS && (
              <button
                type='button'
                className='ta-link ta-proposal__more'
                onClick={() =>
                  setExpandedProposals((prev) => {
                    const next = new Set(prev)
                    if (next.has(item.id)) next.delete(item.id)
                    else next.add(item.id)
                    return next
                  })
                }
              >
                {expanded
                  ? t('AI.SHOW_FEWER')
                  : t('AI.SHOW_ALL_CHANGES', {count: item.changes.length})}
              </button>
            )}

            {item.warnings.length > 0 && (
              <ul className='ta-proposal__warnings'>
                {item.warnings.map((w, idx) => (
                  <li key={idx}>{w}</li>
                ))}
              </ul>
            )}

            {pending && (
              <>
                {!settingsOnly && (
                  <p className='ta-proposal__projection'>
                    {item.saved
                      ? t('AI.SAVED_TO_PROJECTED', {
                          saved: item.saved.filled,
                          filled: projected.filled,
                          total: projected.total,
                        })
                      : t('AI.AFTER_APPLYING', {filled: projected.filled, total: projected.total})}
                  </p>
                )}
                <div className='ta-proposal__actions'>
                  <button
                    type='button'
                    className={removalOnly ? 'ta-btn ta-btn--danger' : 'ta-btn ta-btn--primary'}
                    disabled={versi.isApplying || versi.isLoading}
                    onClick={versi.apply}
                  >
                    {versi.isApplying && (
                      <span className='spinner-border spinner-border-sm' aria-hidden />
                    )}
                    {removalOnly
                      ? t('AI.APPLY_REMOVAL', {count: removals.length})
                      : t('AI.APPLY_CHANGES', {count: item.changes.length})}
                  </button>
                  <button
                    type='button'
                    className='ta-btn ta-btn--outline'
                    disabled={versi.isApplying}
                    onClick={onPreviewInGrid}
                  >
                    <LayoutGrid size={18} aria-hidden />
                    {t('AI.PREVIEW_IN_GRID')}
                  </button>
                  <button
                    type='button'
                    className='ta-btn ta-btn--text'
                    disabled={versi.isApplying}
                    onClick={versi.discard}
                  >
                    {t('AI.DISCARD')}
                  </button>
                </div>
                <p className='ta-proposal__caption'>{t('AI.PREVIEW_CAPTION')}</p>
              </>
            )}
          </div>
          {pending && (
            <div className='ta-bubble'>
              <p>{t('AI.REVISE_HINT')}</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  const renderItem = (item: VersiItem) => {
    switch (item.kind) {
      case 'user':
        return (
          <div key={item.id} className='ta-msg ta-msg--user'>
            <div className='ta-bubble ta-bubble--user'>{item.text}</div>
          </div>
        )
      case 'assistant':
        return (
          <div key={item.id} className='ta-msg ta-msg--assistant'>
            <Avatar />
            <div className='ta-bubble'>
              <p>{item.text}</p>
            </div>
          </div>
        )
      case 'proposal':
        return renderProposal(item)
      case 'clarify':
        return (
          <div key={item.id} className='ta-msg ta-msg--assistant'>
            <Avatar />
            <div className='ta-msg__stack'>
              <div className='ta-bubble'>
                <p>{item.question || t('AI.CLARIFY_DEFAULT')}</p>
              </div>
              <div className='ta-choices' role='group' aria-label={t('AI.CLARIFY_DEFAULT')}>
                {item.options.map((option) => (
                  <button
                    key={option.value ?? option.employeeId}
                    type='button'
                    className='ta-choice'
                    disabled={item.answered || versi.isLoading}
                    onClick={() => versi.answerClarification(item.id, option)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )
      case 'result':
        return (
          <div key={item.id} className='ta-msg ta-msg--assistant'>
            <Avatar />
            <div className='ta-msg__stack'>
              <div className='ta-bubble ta-bubble--success'>
                <p>
                  {item.settingsOnly
                    ? t('AI.RESULT_SETTINGS_ONLY', {count: item.applied})
                    : t('AI.RESULT', {count: item.applied, filled: item.filled, total: item.total})}
                </p>
                <div className='ta-bubble__actions'>
                  <button
                    type='button'
                    className='ta-btn ta-btn--outline ta-btn--sm'
                    onClick={onViewGrid}
                  >
                    <LayoutGrid size={16} aria-hidden />
                    {t('AI.VIEW_UPDATED_GRID')}
                  </button>
                  {!item.undone && versi.canUndo(item.undoId) && (
                    <button
                      type='button'
                      className='ta-btn ta-btn--ghost ta-btn--sm'
                      onClick={() => versi.undoResult(item.id)}
                    >
                      <RotateCcw size={15} aria-hidden />
                      {t('AI.UNDO')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      case 'notice':
        return (
          <div key={item.id} className='ta-msg ta-msg--assistant'>
            <Avatar />
            <div className={`ta-bubble ta-bubble--${item.tone}`}>
              <p>{t(`AI.NOTICE_${item.textKey}`)}</p>
              {item.retry && versi.retry && (
                <div className='ta-bubble__actions'>
                  <button
                    type='button'
                    className='ta-btn ta-btn--outline ta-btn--sm'
                    disabled={versi.isLoading}
                    onClick={versi.retry}
                  >
                    <RotateCcw size={15} aria-hidden />
                    {t('AI.RETRY')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )
      default:
        return null
    }
  }

  const examplesBlock = (
    <div className='ta-examples'>
      <div className='ta-examples__label'>
        <strong>{t('AI.TRY_ASKING')}</strong> {t('AI.TRY_ASKING_HINT')}
      </div>
      {[...examples, ...(moreExamples ? moreExampleList : [])].map((example) => (
        <button
          key={example}
          type='button'
          className='ta-example'
          onClick={() => fillExample(example)}
        >
          <span>{example}</span>
          <ArrowUpRight size={18} aria-hidden />
        </button>
      ))}
      {/* Offered only when there are more examples to show (the server may send none). */}
      {moreExampleList.length > 0 && (
        <button type='button' className='ta-link' onClick={() => setMoreExamples((v) => !v)}>
          {moreExamples ? t('AI.FEWER_EXAMPLES') : t('AI.MORE_EXAMPLES')}
          {moreExamples ? (
            <ChevronUp size={16} aria-hidden />
          ) : (
            <ChevronDown size={16} aria-hidden />
          )}
        </button>
      )}
    </div>
  )

  return (
    <div className='ta-page ta-ai-page'>
      <header className='ta-page__header'>
        <h1 className='ta-page__title'>{t('AI.TITLE')}</h1>
        <p className='ta-page__subtitle'>{t('AI.SUBTITLE')}</p>
      </header>

      <section className='ta-card ta-ai-page__bar'>
        <div className='ta-ai-page__progress' aria-live='polite'>
          <div className='ta-ai-page__progress-text'>
            <strong>
              {t('PROGRESS.ASSIGNMENTS_FILLED', {filled: counts.filled, total: counts.total})}
            </strong>
            <span>{t('PROGRESS.REMAINING', {count: counts.remaining})}</span>
          </div>
          <AllocationProgressBar counts={counts} label={t('PROGRESS.ARIA')} />
        </div>
        <div className='ta-ai-page__scope'>
          <span className='ta-ai-page__scope-label'>{t('AI.WORKING_ON')}</span>
          <AllocationFilterSelect
            label={t('FILTER.BRANCH')}
            value={branch}
            values={branches.values}
            hasAll={branches.hasAll}
            allLabel={t('FILTER.ALL')}
            optionLabel={branchLabel}
            disabled={lockSingleOption && !branches.hasAll && branches.values.length <= 1}
            onChange={onBranchChange}
          />
          <AllocationFilterSelect
            label={t('FILTER.LEVEL')}
            value={level}
            values={levels.values}
            hasAll={levels.hasAll}
            allLabel={t('FILTER.ALL')}
            optionLabel={levelLabel}
            disabled={lockSingleOption && !levels.hasAll && levels.values.length <= 1}
            onChange={onLevelChange}
          />
        </div>
        <button type='button' className='ta-btn ta-btn--outline' onClick={onViewGrid}>
          <LayoutGrid size={20} aria-hidden />
          {t('LANDING.VIEW_GRID')}
        </button>
      </section>

      <section className='ta-card ta-chat' aria-label={t('AI.ASSISTANT_NAME')}>
        <div className='ta-chat__head'>
          <Avatar />
          <div>
            <div className='ta-chat__name'>{t('AI.ASSISTANT_NAME')}</div>
            <div className='ta-chat__role'>{t('AI.ASSISTANT_ROLE')}</div>
          </div>
          <button
            type='button'
            className='ta-btn ta-btn--ghost ta-btn--sm ta-chat__new'
            disabled={!versi.canStartNewChat}
            onClick={() => {
              versi.startNewChat()
              setDraft('')
              setMoreExamples(false)
            }}
          >
            <MessageSquarePlus size={16} aria-hidden />
            {t('AI.NEW_CHAT')}
          </button>
        </div>

        <div className='ta-chat__thread' ref={threadRef} aria-live='polite'>
          {loadingConversation ? (
            <ChatSkeleton />
          ) : (
            <>
              <div className='ta-msg ta-msg--assistant'>
                <Avatar />
                <div className='ta-bubble'>
                  <p>{t('AI.WELCOME_HELLO')}</p>
                  <p>{welcomeLine}</p>
                </div>
              </div>
              {versi.examplesCollapsed ? (
                <div className='ta-examples ta-examples--collapsed'>
                  <button
                    type='button'
                    className='ta-link'
                    onClick={() => versi.setExamplesCollapsed(false)}
                  >
                    {t('AI.SHOW_EXAMPLES')}
                    <ChevronDown size={16} aria-hidden />
                  </button>
                </div>
              ) : (
                <>
                  {examplesBlock}
                  {versi.hasConversation && (
                    <button
                      type='button'
                      className='ta-link ta-examples__hide'
                      onClick={() => versi.setExamplesCollapsed(true)}
                    >
                      {t('AI.HIDE_EXAMPLES')}
                      <ChevronUp size={16} aria-hidden />
                    </button>
                  )}
                </>
              )}

              {versi.items.map(renderItem)}
            </>
          )}

          {versi.isLoading && !loadingConversation && (
            <div className='ta-msg ta-msg--assistant'>
              <Avatar />
              <div className='ta-bubble ta-bubble--typing' role='status'>
                <span />
                <span />
                <span />
                <span className='visually-hidden'>{t('AI.THINKING')}</span>
              </div>
            </div>
          )}
        </div>

        <div ref={composerRef}>
          <VersiComposer
            flagLowConfidence={flagLowConfidence}
            value={draft}
            onChange={setDraft}
            onSend={send}
            disabled={versi.isLoading || versi.isApplying}
            placeholder={versi.pendingItem ? t('AI.PLACEHOLDER_REVISE') : t('AI.PLACEHOLDER')}
          />
        </div>
      </section>

      {footer}
    </div>
  )
}

export default VersiAllocationPage
